/**
 * Brand feature — API client.
 *
 * Mirrors the backend brand routes. Used by BrandPage and other components
 * that need brand data (e.g. EditorPage for modify-with-brand).
 */

import { apiFetch } from './api';

// ---------------------------------------------------------------------------
// Types (shared with backend — keep in sync with src/lib/brand.ts)
// ---------------------------------------------------------------------------

export interface BrandColors {
  primary: string | null;
  secondary: string | null;
  accent: string | null;
  neutral: string | null;
}

export interface BrandProfile {
  brand_name: string | null;
  one_liner: string | null;
  audience_description: string | null;
  voice_descriptors: string[];
  do_say: string[];
  dont_say: string[];
  signature_phrases: string[];
  typical_cta_style: string | null;
  colors: BrandColors;
  logo_asset_id: string | null;
  manual_overrides: string[];
  source: 'distilled' | 'questionnaire' | 'hybrid';
  last_distilled_at: number | null;
}

export type BrandAssetType =
  | 'brand_logo'
  | 'brand_guidelines'
  | 'previous_email'
  | 'product_shot'
  | 'voice_doc'
  | 'about_doc'
  | 'other';

export type BrandAssetStatus = 'uploaded' | 'processing' | 'ingested' | 'failed';

export interface BrandAsset {
  id: string;
  user_id: string;
  asset_type: BrandAssetType;
  r2_key: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  extracted_text: string | null;
  ai_notes: string | null;
  status: BrandAssetStatus;
  error_message: string | null;
  processing_started_at: number | null;
  created_at: number;
  updated_at: number;
}

export const ASSET_TYPE_LABELS: Record<BrandAssetType, string> = {
  brand_logo: 'Logo',
  brand_guidelines: 'Brand Guidelines',
  previous_email: 'Past Email',
  product_shot: 'Product Image',
  voice_doc: 'Voice & Tone',
  about_doc: 'About / Company Info',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchBrandProfile(): Promise<BrandProfile | null> {
  const data = await apiFetch<{ profile: BrandProfile | null }>('/api/brand/profile');
  return data.profile ?? null;
}

export async function patchBrandProfile(
  patches: Partial<BrandProfile>,
): Promise<BrandProfile> {
  const data = await apiFetch<{ profile: BrandProfile }>('/api/brand/profile', {
    method: 'PATCH',
    json: patches,
  });
  return data.profile;
}

export async function triggerDistill(): Promise<BrandProfile> {
  const data = await apiFetch<{ profile: BrandProfile }>('/api/brand/profile/distill', {
    method: 'POST',
    json: {},
  });
  return data.profile;
}

export async function fetchBrandAssets(): Promise<BrandAsset[]> {
  const data = await apiFetch<{ assets: BrandAsset[] }>('/api/brand/assets');
  return data.assets ?? [];
}

export async function uploadBrandAsset(
  file: File,
  assetType: BrandAssetType,
): Promise<BrandAsset> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('asset_type', assetType);

  const data = await apiFetch<{ asset: BrandAsset }>('/api/brand/assets', {
    method: 'POST',
    body: formData,
  });
  return data.asset;
}

export async function deleteBrandAsset(id: string): Promise<void> {
  await apiFetch(`/api/brand/assets/${id}`, { method: 'DELETE' });
}

export async function reprocessBrandAsset(id: string): Promise<void> {
  await apiFetch(`/api/brand/assets/${id}/reprocess`, { method: 'POST' });
}

